import React, { useState } from 'react'
import _ from 'lodash'
import styled from 'styled-components'
import { OverlayTrigger, Table, Tooltip } from 'react-bootstrap'

import ActionConfirmationInputDialog from 'live/components/actions/ui/confirmation-input-dialog'
import i18n from 'live/services/i18n'

import { Index } from './collections-table'
import { bytesToSize } from '../../../lib/utils'

interface IndexesTableProps {
    indexes: Index[]
    readonly: boolean
    deleteIndex: (selectedIndex: Index) => void
}

const IndexesTable = ({ indexes, deleteIndex, readonly }: IndexesTableProps): JSX.Element => {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState<Index | null>(null)

    const toggleConfirmationModal = (selectedIndex: Index): void => {
        setShowConfirmationModal(oldState => !oldState)
        setSelectedIndex(selectedIndex)
    }

    return (
        <Table className="indexes-table table-fixed">
            <tbody>
                {_.map(indexes, function (index) {
                    const sparseText = index.sparse ? i18n('yes') : i18n('no')
                    const isId = index.name.indexOf('_id_') !== -1
                    const isTimestamp = index.name.indexOf('timestamp_') === 0

                    return (
                        <tr key={index.name}>
                            <td>
                                <div className="index-header">
                                    <div className="index-fields">{JSON.stringify(index.key)}</div>
                                    <div className="index-info">
                                        <ul className="list-inline">
                                            <li>
                                                <span>{i18n('type')}: </span>{' '}
                                                {isId || isTimestamp ? i18n('default') : i18n('custom')}
                                            </li>
                                            <li>
                                                {i18n('Size on disk')}: {bytesToSize(index.size)}
                                            </li>
                                            <li>
                                                <span
                                                    title={i18n(
                                                        "Doesn't index documents that don't contain the indexed fields"
                                                    )}
                                                >
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id="totalIndexesindex">
                                                                {i18n(
                                                                    "Doesn't index documents that don't contain the indexed fields"
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <div>
                                                            {i18n('Sparse')}: {sparseText}
                                                        </div>
                                                    </OverlayTrigger>
                                                </span>
                                            </li>
                                            {index.partialFilterExpression && (
                                                <li>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id="partial-filter-index">
                                                                {i18n(
                                                                    'Only index documents when this expression evaluates to true'
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <div>
                                                            {i18n('Partial filter expression')}:{' '}
                                                            {JSON.stringify(index.partialFilterExpression)}
                                                        </div>
                                                    </OverlayTrigger>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                </div>

                                {!isId && !readonly && (
                                    <a className="btn-delete" onClick={() => toggleConfirmationModal(index)}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={<Tooltip id="delete">{i18n('Delete')}</Tooltip>}
                                        >
                                            <i className="far fa-trash-alt fa-fw" />
                                        </OverlayTrigger>
                                    </a>
                                )}
                            </td>
                        </tr>
                    )
                })}

                <ActionConfirmationInputDialog
                    show={showConfirmationModal}
                    onHide={toggleConfirmationModal}
                    className="confirmation-input-modal modal-error modal-sm"
                    confirmationText={i18n('Delete')}
                    content={
                        <CollectionModalAlertContainer className="modal-confirmation-alert">
                            <p className="mongodb-modal-title modal-confirmation-alert">
                                <span>{i18n('Are you sure you want to delete the index')} </span>
                                {`"`}
                                <b>{selectedIndex?.name}</b>
                                {`"`}
                                <span> {i18n('from the collection')} </span>
                                {`"`}
                                <b>{selectedIndex?.collection}</b>
                                {`"`} ?
                            </p>
                            <strong className="msg-error">{i18n('This action cannot be undone')}.</strong>
                            <p>
                                {i18n(
                                    'This action does not erase data, but it can affect datasources, rules and dashboards'
                                )}
                                .
                            </p>
                            <p>
                                <span>{i18n('If you really want to delete the index, type')} </span>
                                <span className="remove-text">{i18n('Delete')} </span>
                                <span>{i18n('in the field below before completing the action')}:</span>
                            </p>
                        </CollectionModalAlertContainer>
                    }
                    btnText={i18n('Delete index')}
                    apply={deleteIndex(selectedIndex)}
                />
            </tbody>
        </Table>
    )
}

export default IndexesTable

const CollectionModalAlertContainer = styled.div`
    p.mongodb-modal-title {
        word-break: break-all;
    }
`
